import React, {useEffect, useId, useMemo, useRef, useState} from 'react'
export interface ProjectTreeOption {
  value: string
  label: string
  programmeName?: string | null
  projectNumber?: string
  isArchived?: boolean
  isDisabled?: boolean
}

interface Props {
  options?: ProjectTreeOption[]
  value?: ProjectTreeOption[]
  onChange: (projects: ProjectTreeOption[]) => void
  onBlur?: () => void
  isDisabled?: boolean
  loading?: boolean
  labelledBy?: string
  inputId?: string
  maxHeight?: number
  framed?: boolean
}

interface ProjectGroup {
  key: string
  name: string
  projects: ProjectTreeOption[]
}

const EMPTY_PROJECTS: ProjectTreeOption[] = []

const GroupCheckbox: React.FC<{
  id: string
  checked: boolean
  partial: boolean
  disabled: boolean
  onChange: (checked: boolean) => void
}> = ({id, checked, partial, disabled, onChange}) => {
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = partial
  }, [partial])
  return (
    <input
      ref={ref}
      id={id}
      className='form-check-input flex-shrink-0 m-0'
      type='checkbox'
      checked={checked}
      aria-checked={partial ? 'mixed' : checked}
      disabled={disabled}
      onChange={(event) => onChange(event.target.checked)}
    />
  )
}

const ProjectSelectionTree: React.FC<Props> = ({
  options = EMPTY_PROJECTS,
  value = EMPTY_PROJECTS,
  onChange,
  onBlur,
  isDisabled = false,
  loading = false,
  labelledBy = 'assigned-projects-label',
  inputId = 'selectedProjects',
  maxHeight = 320,
  framed = true,
}) => {
  const id = useId()
  const [search, setSearch] = useState('')
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set())
  const selected = useMemo(() => new Set(value.map((project) => project.value)), [value])
  const groups = useMemo(() => {
    const available = new Map(options.map((project) => [project.value, project]))
    const projects = new Map(available)
    value.forEach((project) => {
      if (!projects.has(project.value)) projects.set(project.value, project)
    })
    const grouped = new Map<string, ProjectGroup>()
    projects.forEach((project) => {
      const programme = project.programmeName?.trim()
      const key = !available.has(project.value) ? 'unavailable' : programme ? `programme:${programme.toLowerCase()}` : 'none'
      const name = key === 'unavailable' ? 'Other assigned projects' : programme || 'No programme'
      if (!grouped.has(key)) grouped.set(key, {key, name, projects: []})
      grouped.get(key)!.projects.push(project)
    })
    return Array.from(grouped.values())
      .sort((a, b) => {
        if (a.key === 'none' || a.key === 'unavailable') return b.key === 'none' || b.key === 'unavailable' ? a.name.localeCompare(b.name) : 1
        if (b.key === 'none' || b.key === 'unavailable') return -1
        return a.name.localeCompare(b.name)
      })
      .map((group) => ({...group, projects: group.projects.sort((a, b) => a.label.localeCompare(b.label) || a.value.localeCompare(b.value))}))
  }, [options, value])

  const query = search.trim().toLowerCase()
  const visibleGroups = groups.map((group) => ({
    ...group,
    visibleProjects: !query || group.name.toLowerCase().includes(query)
      ? group.projects
      : group.projects.filter((project) => `${project.label} ${project.projectNumber ?? ''}`.toLowerCase().includes(query)),
  })).filter((group) => group.visibleProjects.length > 0)

  const updateSelection = (projects: ProjectTreeOption[], checked: boolean) => {
    const next = new Map(value.map((project) => [project.value, project]))
    projects.filter((project) => !project.isDisabled).forEach((project) => {
      if (checked) next.set(project.value, project)
      else next.delete(project.value)
    })
    onChange(Array.from(next.values()))
  }

  return (
    <div
      className={framed ? 'border rounded p-3' : 'p-3'}
      onBlur={onBlur}
      onKeyDown={(event) => { if (event.key === 'Enter') event.stopPropagation() }}
      aria-busy={loading}
      role='group'
      aria-labelledby={labelledBy}
    >
      <input
        id={inputId}
        type='search'
        className='form-control form-control-sm mb-3'
        aria-label='Search programmes or projects'
        placeholder='Search programmes or projects'
        value={search}
        disabled={isDisabled || loading}
        onKeyDown={(event) => { if (event.key === 'Enter') event.preventDefault() }}
        onChange={(event) => {
          setSearch(event.target.value)
          setCollapsed(new Set())
        }}
      />
      {loading && <div className='text-muted py-2' role='status'>Loading projects...</div>}
      <ul className='list-unstyled mb-0 overflow-auto' style={{maxHeight}}>
        {visibleGroups.map((group, groupIndex) => {
          const selectable = group.projects.filter((project) => !project.isDisabled)
          const checkedCount = selectable.filter((project) => selected.has(project.value)).length
          const checked = selectable.length > 0 && checkedCount === selectable.length
          const partial = checkedCount > 0 && !checked
          const expanded = !collapsed.has(group.key)
          const groupId = `${id}-group-${groupIndex}`
          return (
            <li key={group.key}>
              <div className='d-flex align-items-center gap-3 py-2'>
                <button
                  type='button'
                  className='btn btn-sm btn-icon btn-light flex-shrink-0'
                  style={{width: 28, height: 28}}
                  aria-expanded={expanded}
                  aria-controls={`${groupId}-projects`}
                  title={`${expanded ? 'Collapse' : 'Expand'} ${group.name}`}
                  aria-label={`${expanded ? 'Collapse' : 'Expand'} ${group.name}`}
                  onClick={() => setCollapsed((previous) => {
                    const next = new Set(previous)
                    if (next.has(group.key)) next.delete(group.key)
                    else next.add(group.key)
                    return next
                  })}
                >
                  <i className={`bi bi-chevron-${expanded ? 'down' : 'right'}`} aria-hidden='true' />
                </button>
                <GroupCheckbox
                  id={groupId}
                  checked={checked}
                  partial={partial}
                  disabled={isDisabled || loading || selectable.length === 0}
                  onChange={(nextChecked) => updateSelection(group.projects, nextChecked)}
                />
                <label className='fw-semibold mb-0 text-break flex-grow-1' htmlFor={groupId} style={{minWidth: 0}}>
                  {group.name}
                </label>
                <span className='text-muted fs-8 flex-shrink-0'>{checkedCount}/{selectable.length}</span>
              </div>
              <ul id={`${groupId}-projects`} className='list-unstyled ms-10 ps-6 border-start mb-2' hidden={!expanded}>
                {group.visibleProjects.map((project, projectIndex) => {
                  const projectId = `${groupId}-project-${projectIndex}`
                  return (
                    <li key={project.value} className='py-2'>
                      <label className='d-flex align-items-start gap-3 mb-0' htmlFor={projectId}>
                        <input
                          id={projectId}
                          type='checkbox'
                          className='form-check-input flex-shrink-0 mt-1'
                          checked={selected.has(project.value)}
                          disabled={isDisabled || loading || project.isDisabled}
                          onChange={(event) => updateSelection([project], event.target.checked)}
                        />
                        <span className='text-break' style={{minWidth: 0}}>
                          {project.label}
                          {project.isArchived && <span className='badge badge-light-secondary ms-2'>Archived</span>}
                          {project.projectNumber && <span className='d-block text-muted fs-8'>{project.projectNumber}</span>}
                        </span>
                      </label>
                    </li>
                  )
                })}
              </ul>
            </li>
          )
        })}
      </ul>
      {!loading && visibleGroups.length === 0 && (
        <div className='text-muted py-3'>{query ? 'No matching projects' : 'No projects available'}</div>
      )}
    </div>
  )
}

export default ProjectSelectionTree
